<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import GlobalSearchSection from '@/components/global-search-section.vue';
import Header from '@/components/Header/Header';
import Footer from '@/components/Footer/Footer.vue';
import SliderItem from '@/components/Slider/SliderItem.vue';
import SliderItemSport from '@/components/Slider/SliderItemSport.vue';
import SliderLiveTv from '@/components/Slider/SliderLiveTv.vue';
import Tabs from '@/components/Tabs/Tabs.vue';
import SearchIcon from '@/assets/icons/search.svg';
import SearchPageEmptyState from '@/views/Search/components/search-page-empty-state.vue';
import SearchPageHeading from '@/views/Search/components/search-page-heading.vue';
import SearchPageResults from '@/views/Search/components/search-page-results.vue';
import { useSearchPage } from '@/views/Search/composables/use-search-page';
import { SEARCH_ROUTE_QUERY, SEARCH_TAB_OPTIONS, SEARCH_TABS } from '@/views/Search/constants';

const store = useStore();
const { t } = useI18n();

const {
  query,
  searchedQuery,
  activeTab,
  isAllTab,
  activeTabOption,
  fallbackTabOption,
  activeResults,
  activeSections,
  resultsCount,
  status,
  hasNextPage,
  loading,
  loadingMore,
  submit,
  setActiveTab,
  clear,
  loadMore,
} = useSearchPage();

const isActive = computed(() => store.state.infoMovie.isActive);
const brandImage = computed(() => `/img/${store.state.config?.brandName}-logo.png`);

const resultViews = computed(() => ({
  [SEARCH_TABS.movies]: { component: SliderItem, itemProps: { brandImage: brandImage.value } },
  [SEARCH_TABS.tvShows]: { component: SliderItem, itemProps: { brandImage: brandImage.value } },
  [SEARCH_TABS.sportsVod]: { component: SliderItemSport, itemProps: {} },
  [SEARCH_TABS.liveTv]: { component: SliderLiveTv, itemProps: {} },
}));

const activeResultView = computed(() => resultViews.value[activeTab.value]);

const getTabTitle = (tab) => t(SEARCH_TAB_OPTIONS.find(({ id }) => id === tab).text);

const getViewAllRoute = (tab) => ({
  name: 'Search',
  query: { [SEARCH_ROUTE_QUERY.query]: searchedQuery.value, [SEARCH_ROUTE_QUERY.tab]: tab },
});

const headingTitle = computed(() => (searchedQuery.value ? t('search.resultsFor', { query: searchedQuery.value }) : t('search.pageTitle')));

const headingMeta = computed(() => {
  if (status.value === 'idle') return t('search.pageSubtitle');
  if (status.value === 'loading') return t('search.searching');
  return t('search.resultsCount', { count: resultsCount.value });
});

const headingCategory = computed(() => (searchedQuery.value ? t(activeTabOption.value.text) : ''));
</script>

<template>
  <div class="db" :class="{ show: isActive }">
    <div class="header-wrap">
      <Header :hideSearch="true" />
    </div>
    <main class="search-page">
      <SearchPageHeading :title="headingTitle" :meta="headingMeta" :category="headingCategory" />

      <Tabs class="search-page__tabs" :activetab="activeTab" :setActiveTab="setActiveTab" :tabs="SEARCH_TAB_OPTIONS" />

      <form class="search-page__form" role="search" @submit.prevent="submit">
        <input id="search-page-query" v-model="query" type="search" class="search-page__input" :placeholder="t(activeTabOption.placeholder)" :aria-label="t(activeTabOption.placeholder)" />
        <button type="submit" class="search-page__submit" :aria-label="t('search.pageTitle')">
          <SearchIcon />
        </button>
      </form>

      <SearchPageEmptyState v-if="status === 'idle'" icon="⌕" :title="t('search.emptyTitle')" :text="t('search.emptyText')" />

      <SearchPageEmptyState v-else-if="status === 'empty'" icon="∅" :title="t('search.noResultsTitle', { query: searchedQuery })" :text="t('search.noResultsText')">
        <template #actions>
          <button v-if="fallbackTabOption" type="button" class="search-page__button search-page__button--primary" @click="setActiveTab(fallbackTabOption.id)">
            {{ t('search.searchIn', { category: t(fallbackTabOption.text) }) }}
          </button>
          <button type="button" class="search-page__button" @click="clear">{{ t('search.clear') }}</button>
        </template>
      </SearchPageEmptyState>

      <div v-else-if="isAllTab && status === 'results'" class="search-page__sections">
        <GlobalSearchSection
          v-for="section in activeSections"
          :key="section.tab"
          :title="getTabTitle(section.tab)"
          :items="section.items"
          :component="resultViews[section.tab].component"
          :item-props="resultViews[section.tab].itemProps"
          :view-all-route="getViewAllRoute(section.tab)"
        />
      </div>

      <SearchPageResults
        v-else
        :key="activeTab"
        :items="activeResults"
        :component="activeResultView?.component"
        :item-props="activeResultView?.itemProps"
        :loading="loading || loadingMore"
        :has-next-page="hasNextPage"
        @load-more="loadMore"
      />
    </main>
    <Footer class="member-footer" />
  </div>
</template>

<style lang="scss" scoped>
.search-page {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 50px;
  padding: 32px 4% 48px;

  &__tabs {
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 0;

    :deep(button) {
      padding: 8px 18px;
      border: 1px solid $black80;
      border-radius: 4px;
      background: $black30;
      color: $light-gray;
      font-size: 0.85rem;
      font-weight: 600;

      &:hover {
        border-color: $white;
        background: $black30;
        color: $white;
      }

      &.active {
        border-color: $bg-red;
        background: $bg-red;
        color: $white;
      }
    }
  }

  &__sections {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  &__form {
    position: relative;
    display: flex;
    max-width: 520px;
  }

  &__input {
    width: 100%;
    height: 44px;
    padding: 0 44px 0 14px;
    border: 1px solid $gray77;
    border-radius: 4px;
    background: $black;
    color: $white;
    font-size: 0.95rem;
    outline: none;

    &:hover,
    &:focus-visible {
      border-color: $white;
    }
  }

  &__submit {
    position: absolute;
    top: 50%;
    right: 10px;
    display: grid;
    place-items: center;
    padding: 0;
    border: 0;
    background: none;
    color: $light-gray;
    cursor: pointer;
    transform: translateY(-50%);
  }

  &__button {
    padding: 8px 16px;
    border: 1px solid $black80;
    border-radius: 4px;
    background: $black30;
    color: $white;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      border-color: $white;
    }

    &--primary {
      border-color: $bg-red;
      background: $bg-red;

      &:hover,
      &:focus-visible {
        border-color: $white;
      }
    }
  }
}
</style>

<style lang="scss">
.db {
  &.show {
    height: 100.1vh;
    overflow: hidden;
  }
}
</style>
